import { useEffect, useRef, useState } from 'react';
import { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';

import pdfjs from 'PDF/services/PDFJS';
import { useDownloadQuery } from '_common/services/api/FileApi';
import { usePresentationPreviewQuery } from 'Presentation/api';

import ObjectPreviewControls from './PreviewControls/PreviewControls';
import { useObjectPreview } from '../ObjectPreviewContext';

type PDFPreviewProps = {
  object: doDOC.File;
  sourceType: 'pdf' | 'presentation';
};

const PDFPreview = ({ object, sourceType }: PDFPreviewProps) => {
  const { setLoading } = useObjectPreview();

  const { data: pdfData } = useDownloadQuery(
    { object_id: object.id },
    { skip: sourceType === 'presentation' },
  );
  const {
    data: pptData,
    isLoading: pptLoading,
    refetch: refetchPPT,
  } = usePresentationPreviewQuery({ object_id: object.id }, { skip: sourceType === 'pdf' });
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState({ label: '100%', value: '1' });
  const pptRefectRef = useRef<NodeJS.Timeout | null>(null);
  const pdfTaskRunning = useRef<PDFDocumentLoadingTask | null>(null);
  const pptTaskRunning = useRef<PDFDocumentLoadingTask | null>(null);

  useEffect(() => {
    setLoading(pptLoading);
  }, [pptLoading]);

  useEffect(() => {
    if (pdfData) {
      pdfData.arrayBuffer().then((arraybuffer) => {
        if (!pdfTaskRunning.current) {
          pdfTaskRunning.current = pdfjs.getDocument({ data: arraybuffer });
          pdfTaskRunning.current.promise.then((pdf) => {
            setPdf(pdf);
            pdfTaskRunning.current = null;
          });
        }
      });
    }
  }, [pdfData]);

  useEffect(() => {
    if (!pdf && pptData) {
      if (pptData instanceof Blob) {
        pptData.arrayBuffer().then((arraybuffer) => {
          if (!pptTaskRunning.current) {
            pptTaskRunning.current = pdfjs.getDocument({ data: arraybuffer });
            pptTaskRunning.current.promise.then((pdf) => {
              setPdf(pdf);
              pptTaskRunning.current = null;
            });
          }
        });
      } else if (pptData.status === 'queued') {
        //Target document hasn't been converted yet, refetch after 1second
        if (pptRefectRef.current) {
          clearInterval(pptRefectRef.current);
          pptRefectRef.current = null;
        }
        pptRefectRef.current = setTimeout(() => {
          refetchPPT();
        }, 1000);
      }
    }

    return () => {
      if (pptRefectRef.current) {
        clearInterval(pptRefectRef.current);
        pptRefectRef.current = null;
      }
    };
  }, [pptData]);

  if (pdf) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Page pdf={pdf} pageNumber={pageNumber} zoom={zoom.value} />
        <ObjectPreviewControls
          zoom={zoom}
          setZoom={setZoom}
          currentPage={pageNumber}
          totalPageNumber={pdf.numPages}
          nextPageIsDisabled={pageNumber === pdf.numPages}
          previousPageIsDisabled={pageNumber === 1}
          onNextPage={() => {
            setPageNumber(Math.min(pdf.numPages, pageNumber + 1));
          }}
          onPreviousPage={() => {
            setPageNumber(Math.max(1, pageNumber - 1));
          }}
          object={object}
        />
      </div>
    );
  }
  return null;
};

type PageProps = {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  zoom: string;
};

const Page = ({ pdf, pageNumber, zoom }: PageProps) => {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    pdf.getPage(pageNumber).then((page) => {
      const viewport = page.getViewport({ scale: +zoom * 1.3 });

      // Prepare canvas using PDF page dimensions
      const canvasContainer = canvasRef.current;

      if (!canvasContainer) {
        return;
      }

      if (canvasContainer.firstChild) {
        canvasContainer.removeChild(canvasContainer.firstChild);
      }
      const canvas = document.createElement('canvas');
      if (canvas) {
        canvasContainer.appendChild(canvas);
        const context = canvas.getContext('2d');
        if (context) {
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          // Render PDF page into canvas context
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };
          page.render(renderContext);
        }
      }
    });
  }, [zoom, pdf, pageNumber]);
  return <div ref={canvasRef} />;
};

export default PDFPreview;
